if (!customElements.get('sticky-header')) {
  /**
   * Custom element for sticky header functionality
   * Handles scroll-based header visibility, positioning, and animations
   * @extends HTMLElement
   */
  class StickyHeader extends HTMLElement {
    /**
     * Creates an instance of StickyHeader
     */
    constructor() {
      super();
    }

    /**
     * Called when the element is connected to the DOM
     * Initializes header elements, event listeners, and observers
     */
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerWrapper = this.header?.querySelector('.header-wrapper');

      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();

      this.setHeaderHeight();

      const debouncedOnResize = debounce(() => {
        this.setHeaderHeight();
      }, 100);

      window.addEventListener('resize', debouncedOnResize.bind(this));
    }

    /**
     * Sets the CSS custom property for header height based on wrapper element height
     */
    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.headerWrapper.offsetHeight}px`);
    }

    /**
     * Called when the element is disconnected from the DOM
     * Removes event listeners to prevent memory leaks
     */
    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    /**
     * Creates an IntersectionObserver to track header bounds
     * Used to determine when the header is scrolled past
     */
    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    /**
     * Handles scroll events to show/hide header based on scroll direction
     * Manages header visibility states and animations
     */
    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }
      this.currentScrollTop = scrollTop;
    }

    /**
     * Hides the header when scrolling down
     * Also closes any open menus or search modals
     */
    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    /**
     * Reveals the header when scrolling up
     * Adds sticky and animation classes
     */
    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    /**
     * Resets header to default state when scrolled to top
     * Removes all sticky and animation classes
     */
    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    /**
     * Closes all open menu disclosures in the header
     */
    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => disclosure.close());
    }

    /**
     * Closes the search modal if it exists
     */
    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      this.searchModal.close(false);
    }
  }

  customElements.define('sticky-header', StickyHeader);
}
